import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { VoiceRecorder } from '@/components/voice/VoiceRecorder';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { templatesService, AiAssistRequest, AiAssistResponse, AiProviderName } from '@/services/templates.service';
import { settingsService, AiModelsResponse } from '@/services/settings.service';
import { voiceService } from '@/services/voice.service';
import toast from 'react-hot-toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (response: AiAssistResponse) => void;
  initialBrief?: string;
}

export const AiAssistModal: React.FC<Props> = ({ open, onClose, onApply, initialBrief }) => {
  const [brief, setBrief] = useState('');
  const [category, setCategory] = useState<AiAssistRequest['category']>('marketing');
  const [tone, setTone] = useState<AiAssistRequest['tone']>('promotional');
  const [type, setType] = useState<'STANDARD' | 'CAROUSEL'>('STANDARD');
  const [busy, setBusy] = useState(false);
  const [showDictation, setShowDictation] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  // AI models catalogue
  const [modelsData, setModelsData] = useState<AiModelsResponse | null>(null);
  const [provider, setProvider] = useState<AiProviderName>('openai');
  const [model, setModel] = useState<string>('');
  const loadedRef = useRef(false);

  // Load available models once when modal first opens
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    settingsService.listAiModels().then((d) => {
      setModelsData(d);
      // If we have no persisted choice, use the company default
      setProvider(d.default.provider);
      setModel(d.default.model);
    }).catch(() => { /* silently ignore — submit still works */ });
  }, [open]);

  useEffect(() => { if (open && initialBrief) setBrief(initialBrief); }, [open, initialBrief]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await templatesService.aiAssist({ brief, category, tone, type, provider: modelsData ? provider : undefined, model: modelsData ? model : undefined });
      toast.success(`Generated · ${r.creditsCharged ?? 0} credits charged`);
      onApply(r);
      setBrief('');
    } catch { /* toast already shown by axios interceptor */ } finally { setBusy(false); }
  };

  // Build the model <Select> options (grouped by provider)
  const availableProviders = modelsData?.providers.filter(p => p.available) ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Generate or improve with AI" widthClass="max-w-xl">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">Describe what you want; AI fills in headers, body, footer, and buttons. Always review before submitting.</p>

        <FormField label="Brief" required>
          <div className="mb-1.5 flex justify-end">
            <EnhancePromptButton value={brief} onChange={setBrief} context="WhatsApp template brief" disabled={busy} provider={provider} model={model} />
          </div>
          <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4}
            placeholder="Promotional message announcing 25% off Black Friday for VIP customers; include a Shop Now button to https://mibrand.com/sale" />
          {/* Voice dictation */}
          {showDictation ? (
            <div className="mt-2">
              <VoiceRecorder
                sending={transcribing}
                onDiscard={() => setShowDictation(false)}
                onSend={async (blob) => {
                  setTranscribing(true);
                  try {
                    const audioFile = new File([blob], 'brief.webm', { type: blob.type || 'audio/webm' });
                    const result = await voiceService.transcribe(audioFile);
                    setBrief((prev) => prev ? `${prev} ${result.text}` : result.text);
                    setShowDictation(false);
                    toast.success('Transcribed and appended to brief');
                  } catch {
                    toast.error('Transcription failed');
                  } finally {
                    setTranscribing(false);
                  }
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDictation(true)}
              className="mt-1.5 flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface-alt">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
              </span>
              Dictate brief
            </button>
          )}
        </FormField>

        <FormField label="Format" hint="Carousel: AI drafts 3–5 cards. Add your own media before review. Carousels use Marketing.">
          <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
            {([
              { value: 'STANDARD', label: 'Standard' },
              { value: 'CAROUSEL', label: 'Carousel' },
            ] as const).map((opt) => {
              const active = type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setType(opt.value);
                    if (opt.value === 'CAROUSEL') setCategory('marketing');
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                    active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Category">
            <Select value={category} onChange={(e) => setCategory(e.target.value as AiAssistRequest['category'])}>
              <option value="marketing">Marketing</option>
              <option value="utility">Utility</option>
              {type !== 'CAROUSEL' && <option value="authentication">Authentication</option>}
            </Select>
          </FormField>
          <FormField label="Tone">
            <Select value={tone} onChange={(e) => setTone(e.target.value as AiAssistRequest['tone'])}>
              <option value="promotional">Promotional</option>
              <option value="friendly">Friendly</option>
              <option value="formal">Formal</option>
              <option value="transactional">Transactional</option>
            </Select>
          </FormField>
        </div>

        {/* Model picker — shows when we have catalogue data */}
        <FormField label="Model">
          {/* Models come from the central registry via /settings/ai/models —
              never hardcoded here so adding/removing a model is a one-file
              backend change, not a hunt-and-replace across the frontend. */}
          <Select
            value={`${provider}::${model}`}
            disabled={availableProviders.length === 0}
            onChange={(e) => {
              const [p, m] = e.target.value.split('::') as [AiProviderName, string];
              setProvider(p);
              setModel(m);
            }}
          >
            {availableProviders.length === 0 && (
              <option value="">Loading models…</option>
            )}
            {availableProviders.map(entry => (
              <optgroup key={entry.id} label={entry.label}>
                {entry.models.map(m => (
                  <option key={m.id} value={`${entry.id}::${m.id}`}>{m.label}</option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FormField>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button disabled={busy || !brief} onClick={submit}>
          <span className="inline-flex items-center gap-2"><Sparkles size={14} /> {busy ? 'Generating…' : 'Generate'}</span>
        </Button>
      </div>
    </Modal>
  );
};
